import { ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Users, BarChart3, Settings, LogOut, ShoppingBag, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface OwnerLayoutProps {
  children: ReactNode;
  showTabs?: boolean;
}

const navTabs = [
  { name: 'Orders', href: '/owner-dashboard', tab: 'orders', icon: Package },
  { name: 'Products', href: '/owner-dashboard?tab=products', tab: 'products', icon: ShoppingBag },
  { name: 'Customers', href: '/owner-dashboard?tab=customers', tab: 'customers', icon: Users },
  { name: 'Analytics', href: '/analytics', tab: null, icon: BarChart3 },
  { name: 'Settings', href: '/owner-dashboard?tab=settings', tab: 'settings', icon: Settings },
];

export function OwnerLayout({ children, showTabs = true }: OwnerLayoutProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActiveTab = (tab: string | null, href: string) => {
    // Analytics page
    if (href === '/analytics') {
      return location.pathname === '/analytics';
    }
    // Dashboard tabs
    if (location.pathname === '/owner-dashboard') {
      const currentTab = searchParams.get('tab') || 'orders';
      return tab === currentTab;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 border-b border-slate-800/60 bg-slate-950/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo / Title */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-5 h-5 text-slate-900" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-100">Samboards Owner Portal</h1>
                <p className="text-xs text-slate-500 -mt-0.5">Fulfilment Centre</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-base font-bold text-slate-100">Owner Portal</h1>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {user && (
                <span className="hidden md:block text-sm text-slate-400">
                  {user.email}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>

          {/* Tabs Navigation */}
          {showTabs && (
            <nav className="flex gap-1 -mb-px overflow-x-auto scrollbar-hide pb-px">
              {navTabs.map((navTab) => {
                const isActive = isActiveTab(navTab.tab, navTab.href);
                const Icon = navTab.icon;

                return (
                  <Link
                    key={navTab.name}
                    to={navTab.href}
                    className={cn(
                      "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                      isActive
                        ? "text-emerald-400 border-emerald-400"
                        : "text-slate-400 border-transparent hover:text-slate-100 hover:border-slate-600"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{navTab.name}</span>
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>
    </div>
  );
}